import React from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import type { ChangeEntry } from './SaveHeaderContext';

type Props = {
  open: boolean;
  onClose: (confirmed: boolean) => void | Promise<void>;
  changes: ChangeEntry[];
};

export const ChangeConfirmDialog = ({ open, onClose, changes }: Props) => {
  const { t } = useTranslation();
  const hasChanges = changes.length > 0;

  return (
    <ConfirmationDialog
      isOpen={open}
      onClose={onClose}
      title={t('saveHeader.confirmTitle')}
      ctaText={t('saveHeader.confirmCta')}
      confirmButtonColor="primary"
      loadingButton
      fullScreenOnMobile
      content={
        <Grid container flexDirection="column" gap={1}>
          <Typography variant="body2" color="text.secondary">
            {t('saveHeader.confirmSubtitle', { count: changes.length })}
          </Typography>
          {!hasChanges && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography>{t('saveHeader.noChanges')}</Typography>
            </>
          )}
          {hasChanges && (
            <Grid container flexDirection="column" gap={2} mt={1}>
              {changes.map((c, i) => (
                <Box
                  key={`${c.label}-${i}`}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}>
                  <Box sx={{ px: 2, py: 1, bgcolor: 'mb.ground' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {c.label}
                    </Typography>
                  </Box>
                  <Grid container>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: 2,
                        borderInlineEnd: 1,
                        borderColor: 'divider',
                        wordBreak: 'break-word',
                      }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        {t('saveHeader.before')}
                      </Typography>
                      <Box component="span">{c.from}</Box>
                    </Grid>
                    <Grid item xs={6} sx={{ p: 2, wordBreak: 'break-word' }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        {t('saveHeader.after')}
                      </Typography>
                      <Box component="span">{c.to}</Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      }
    />
  );
};
